/**
 * Application information available on server and client side
 * @packageDocumentation
 * @module AppService
 */
import { Injectable } from "../dependencies/injectable";
import { injector } from "../dependencies/injector";
import { Lang } from "../i18n/languages";
import { FR_LOCALE, Locale } from "../models/locale.model";
import { setDefaultDateLocale } from "../tools/timestamps.tools.tsdate";

export enum Environment {
    /** The development environment (dev) is the environment in which changes to software are developed */
    DEV = "development",
    /** 
     * The purpose of the test environment is to allow human testers to exercise new and changed code via either automated checks or non-automated techniques 
     */
    TEST = "test",
    /** 
     * A stage, staging or pre-production environment is an environment for testing that exactly resembles a production environment.
     * It seeks to mirror an actual production environment as closely as possible and may connect to other production services and data, such as databases. 
     */
    STAGING = "staging",
    /**
     * The training environment is the environment that users directly interact with for their training on the application.
     * It is similar to a staging environment
     */
    TRAINING = "training",
    /**
     * The production environment is also known as live, particularly for servers, as it is the environment that users directly interact with.
     */
    PRODUCTION = "production"
}

export const APP_SERVICE = "AppService";
export interface AppService {
    env: Environment;
    lang: Lang;

    /**
     * Set Service locale and apply locale to TimestampsTools if 'this' is the registered service
     */
    setLocale(locale: Locale): void;

    /**
     * Get a copy of the locale object, this should be used only to copy the app context, not to access values (you can use App.lang and TimestampsSettings.defaultDateLocale)
     */
    getLocale(): Locale;
}

@Injectable(APP_SERVICE, { onServiceRegister: true })
export class DefaultAppService implements AppService {
    public env: Environment;
    protected _locale: Locale;// DateLocale is accessible from timestampsOptions

    /**
     * Access to default lang when serverAppService, to current client lang when clientAppService
     */
    public get lang(): Lang {
        return this._locale.lang;
    }
    /**
     * Access to default lang when serverAppService, to current client lang when clientAppService
     */
    public set lang(newLang: Lang) {
        this._locale.lang = newLang;
    }

    constructor(env: Environment = Environment.DEV, locale: Locale = FR_LOCALE) {
        this.env = env;
        this._locale = locale;
    }

    /**
     * Get a copy of the locale object, this should be used only to copy the app context, not to access values (you can use App.lang and TimestampsSettings.defaultDateLocale)
     */
    public getLocale(): Locale {
        return { ... this._locale };
    }

    /**
     * Set Service locale and apply locale to TimestampsTools if 'this' is the registered service
     */
    public setLocale(locale: Locale): void {
        this._locale = locale;
        if (injector.get<AppService>(APP_SERVICE) === this) {
            this.onServiceRegister();
        }
    }

    /**
     * Function called on service register
     */
    public onServiceRegister(): void {
        setDefaultDateLocale(this._locale);
    }
}